import type { Editor } from '@tiptap/core';
import type { ETiptapAlignmentSelectorKey } from '../../../types/tiptap.types';
import type { Node as PmNode } from '@tiptap/pm/model';
import type { Selection } from '@tiptap/pm/state';
import { CellSelection, cellAround } from '@tiptap/pm/tables';

import { setAttrsAtPos } from '../../extensions/listUtils';
import { ETableCellVerticalAlignment, VERTICAL_ALIGN_ATTRIBUTE } from './cellAttributes';

const selectedCells = (selection: Selection): { node: PmNode; pos: number }[] => {
  const cells: { node: PmNode; pos: number }[] = [];

  if (selection instanceof CellSelection) {
    selection.forEachCell((node, pos) => cells.push({ node, pos }));
    return cells;
  }

  const $cell = cellAround(selection.$head);
  if ($cell?.nodeAfter) cells.push({ node: $cell.nodeAfter, pos: $cell.pos });
  return cells;
};

/** `null` when the selected cells disagree, so no menu item shows as active. */
export const getVerticalAlignmentState = (editor: Editor): ETableCellVerticalAlignment | null => {
  const values = new Set<ETableCellVerticalAlignment | null>(
    selectedCells(editor.state.selection).map(({ node }) => node.attrs[VERTICAL_ALIGN_ATTRIBUTE] ?? null)
  );

  return values.size === 1 ? Array.from(values)[0] : null;
};

export const setVerticalAlignment = (editor: Editor, value: ETableCellVerticalAlignment | null): boolean =>
  editor
    .chain()
    .focus()
    .command(({ tr }) =>
      selectedCells(tr.selection).reduce(
        (changed, { node, pos }) => setAttrsAtPos(tr, pos, node.type, { [VERTICAL_ALIGN_ATTRIBUTE]: value }) || changed,
        false
      )
    )
    .run();

/**
 * Both axes in one transaction, so a single undo puts the cell back. A list carries its alignment
 * on the cell so the marker moves with the text; everything else is aligned per block.
 */
export const setCellAlignment = (
  editor: Editor,
  vertical: ETableCellVerticalAlignment | null,
  horizontal: ETiptapAlignmentSelectorKey
): boolean => {
  const hasList =
    editor.isActive('bulletList') || editor.isActive('orderedListWithStart') || editor.isActive('taskList');

  const chain = editor
    .chain()
    .focus()
    .command(({ tr }) => {
      selectedCells(tr.selection).forEach(({ node, pos }) =>
        setAttrsAtPos(tr, pos, node.type, { [VERTICAL_ALIGN_ATTRIBUTE]: vertical })
      );
      return true;
    });

  return hasList ? chain.setCellAttribute('align', horizontal).run() : chain.setTextAlign(horizontal).run();
};
